import React, { useState } from 'react';
import {
  Box,
  TextField,
  Grid,
  Card,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import { searchStockImages, uploadFromUrl } from '../../services/api';
import { useProject } from '../../contexts/ProjectContext';

const StockImageSearch = ({ onImageSelected, disabled = false }) => {
  const { selectedProject } = useProject();
  const [searchQuery, setSearchQuery] = useState('');
  const [source, setSource] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [showAllProjects, setShowAllProjects] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      setError(null);
      const response = await searchStockImages(
        searchQuery, 
        source, 
        showAllProjects ? null : selectedProject?.id
      );
      setImages(response.images);
    } catch (err) {
      setError('Failed to search images: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = async (image) => {
    try {
      setLoading(true);
      setError(null);
      
      // Upload the image from URL first
      const uploadResult = await uploadFromUrl({
        url: image.download_url,
        prompt: image.description || `Stock image from ${image.source} by ${image.author}`
      });
      
      onImageSelected(uploadResult);
    } catch (err) {
      setError('Failed to select image: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!selectedProject?.id ? (
        <Typography color="error" sx={{ mb: 2 }}>
          Please select a project first to search stock images
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search for images..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              disabled={loading || disabled}
            />
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={source}
                label="Source"
                onChange={(e) => setSource(e.target.value)}
                disabled={loading || disabled}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="unsplash">Unsplash</MenuItem>
                <MenuItem value="pexels">Pexels</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={showAllProjects}
                  onChange={(e) => setShowAllProjects(e.target.checked)}
                />
              }
              label="All Projects"
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!searchQuery.trim() || loading || disabled}
            >
              Search
            </Button>
          </Box>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {images.map((image) => (
                <Grid item xs={12} sm={6} md={4} key={image.id}>
                  <Card sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={image.thumbnail}
                      alt={image.description || 'Stock image'}
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                      }}
                    />
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" display="block" noWrap>
                        {image.description || 'No description'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {image.source} • By {image.author}
                      </Typography>
                    </Box>
                    <Tooltip title="Select Image">
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper'
                        }}
                        onClick={() => handleImageSelect(image)}
                        disabled={disabled}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default StockImageSearch; 